import { useRef, useState } from 'react';
import { Alert, Anchor, Badge, Button, Checkbox, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IconInfoCircle, IconLock } from '@tabler/icons-react';
import {
  OFFER_TERMS_URL,
  RECURRING_CONSENT_LABEL,
  TEST_OFFER_BADGE,
  formatPlanDuration,
  formatRubles,
  type CheckoutProduct,
  type CheckoutStart,
} from '@creativitas/shared';
import { LoadingState, ErrorState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { CHECKOUT_PRODUCTS_KEY, fetchCheckoutProducts, startCheckout } from '@/features/admin/payments-api';

/**
 * Витрина прямой оплаты (docs/payments/ROBOKASSA_DIRECT.md).
 *
 * Цена и то, что даст покупка именно этому покупателю, — с сервера. Кнопка
 * создаёт счёт и уводит в Robokassa в этой же вкладке: оттуда покупатель
 * вернётся на страницу проверки платежа. Права выдаёт только уведомление
 * Robokassa на сервере — не эта страница и не возврат.
 */

export { OFFER_TERMS_URL };
/** Последний счёт — на случай, если Robokassa вернёт без номера. */
export const LAST_CHECKOUT_KEY = 'creativitas.lastCheckout';

const date = (iso: string | null) =>
  // «2 ноября 2028», без «г.»: за датой в тексте идёт своя точка.
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).replace(/\s?г\.$/, '') : '';

export function durationLabel(product: Pick<CheckoutProduct, 'grantType' | 'days' | 'years'>): string {
  if (product.grantType !== 'plan') return 'бессрочно';
  return formatPlanDuration(product.days, product.years);
}

/** Что произойдёт после оплаты — словами покупателя. */
function effectText(product: CheckoutProduct): string | null {
  switch (product.effect) {
    case 'started':
      return product.endsAt ? `Начнёт действовать сразу, до ${date(product.endsAt)}.` : 'Начнёт действовать сразу после оплаты.';
    case 'extended':
      return `Продлит текущий тариф до ${date(product.endsAt)}.`;
    case 'upgraded':
      return `Про — до ${date(product.endsAt)}. Лайт прекратится в момент оплаты${product.lostDays ? `: оставшиеся ${product.lostDays} дн. не переносятся и не возвращаются` : ''}.`;
    case 'owned':
      return product.grantType === 'content_access' ? 'Этот материал у вас уже открыт.' : 'Эта игра уже у вас.';
    case 'blocked':
      return 'Недоступно при действующем Про.';
    case 'new':
      return product.grantType === 'content_access'
        ? 'Материал останется у вас навсегда и не требует тарифа.'
        : 'Игра остаётся у вас навсегда и не требует тарифа.';
  }
}

/** Отправка подписанной сервером формы в Robokassa — переход в этой вкладке. */
export function goToRobokassa(start: CheckoutStart): void {
  try {
    sessionStorage.setItem(LAST_CHECKOUT_KEY, start.checkoutId);
  } catch {
    /* хранилище недоступно — страница возврата возьмёт номер из адреса */
  }
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = start.action;
  form.acceptCharset = 'UTF-8';
  for (const [name, value] of Object.entries(start.fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Ключ нажатия: один на товар в пределах открытой витрины — двойной щелчок не создаёт второй счёт. */
function useClickKeys() {
  const keys = useRef(new Map<string, string>());
  return (code: string) => {
    let key = keys.current.get(code);
    if (!key) {
      key = `${code}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      keys.current.set(code, key);
    }
    return key;
  };
}

export function CheckoutProducts({
  filter,
  compact = false,
}: {
  /** Какие товары показывать (например, только Про или только эту игру). */
  filter?: (product: CheckoutProduct) => boolean;
  compact?: boolean;
}) {
  const catalog = useQuery({ queryKey: CHECKOUT_PRODUCTS_KEY, queryFn: fetchCheckoutProducts, staleTime: 0 });
  const keyFor = useClickKeys();
  // Галочка автопродления по умолчанию снята (оферта, п. 5.2.1: только явное согласие).
  const [recurring, setRecurring] = useState<Record<string, boolean>>({});
  const pay = useMutation({
    mutationFn: ({ code, withRecurring }: { code: string; withRecurring: boolean }) =>
      startCheckout({
        productCode: code,
        // Разовая оплата и с автопродлением — разные счета: у каждой свой ключ нажатия.
        idempotencyKey: keyFor(withRecurring ? `${code}-R` : code),
        ...(withRecurring ? { recurring: true } : {}),
      }),
    onSuccess: goToRobokassa,
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  if (catalog.isLoading) return <LoadingState label="Загружаем тарифы..." />;
  if (catalog.isError || !catalog.data) return <ErrorState onRetry={() => void catalog.refetch()} />;

  const items = catalog.data.items.filter((item) => item.effect !== 'blocked' && (!filter || filter(item)));
  if (items.length === 0) {
    return <Text size="sm" c="var(--text-muted)">Сейчас здесь нечего купить.</Text>;
  }

  return (
    <Stack gap="sm" data-testid="checkout-products">
      {catalog.data.test ? (
        <Group gap="xs">
          <Badge size="sm" variant="light" color="grape">{TEST_OFFER_BADGE}</Badge>
          <Text size="xs" c="var(--text-muted)">Тестовая оплата Robokassa: доступ не выдаётся.</Text>
        </Group>
      ) : null}
      {!catalog.data.emailVerified ? (
        <Alert variant="light" color="yellow" icon={<IconInfoCircle size={16} />} p="xs">
          <Text size="sm">Подтвердите почту по ссылке из письма — после этого можно оплатить.</Text>
        </Alert>
      ) : null}
      <SimpleGrid cols={{ base: 1, sm: compact ? 1 : Math.min(items.length, 3) }} spacing="sm">
        {items.map((product) => {
          const owned = product.effect === 'owned';
          const busy = pay.isPending && pay.variables?.code === product.code;
          const withRecurring = Boolean(product.recurring && recurring[product.code]);
          return (
            <Paper
              key={product.code}
              withBorder
              p="md"
              radius="lg"
              data-testid={`checkout-product-${product.code}`}
              style={{ borderColor: product.plan === 'pro' ? 'var(--gold)' : 'var(--border-subtle)' }}
            >
              <Stack gap={6} h="100%" justify="space-between">
                <Stack gap={4}>
                  <Title order={4}>{product.title}</Title>
                  <Group gap="xs" align="baseline">
                    <Text fw={700} fz={24}>{formatRubles(product.priceKopecks)}</Text>
                    <Text size="sm" c="var(--text-muted)">{durationLabel(product)}</Text>
                  </Group>
                  {product.description ? (
                    <Text size="sm" style={{ whiteSpace: 'pre-line' }} data-testid="checkout-product-description">{product.description}</Text>
                  ) : null}
                  <Text size="sm" c="var(--text-muted)">{effectText(product)}</Text>
                  {product.recurring ? (
                    <Checkbox
                      mt={4}
                      size="sm"
                      data-testid={`recurring-${product.code}`}
                      checked={withRecurring}
                      onChange={(event) => {
                        const checked = event.currentTarget.checked;
                        setRecurring((current) => ({ ...current, [product.code]: checked }));
                      }}
                      // Подпись — дословно по требованию Robokassa, «условиям оферты» — ссылка.
                      label={
                        <>
                          {RECURRING_CONSENT_LABEL.replace(/условиям оферты$/, '')}
                          <Anchor href={OFFER_TERMS_URL} target="_blank" rel="noopener" inherit>условиям оферты</Anchor>
                        </>
                      }
                      description={product.recurring.consentText}
                    />
                  ) : null}
                </Stack>
                <Button
                  mt="xs"
                  fullWidth
                  disabled={owned || !catalog.data.emailVerified || (pay.isPending && !busy)}
                  loading={busy}
                  leftSection={<IconLock size={14} />}
                  // Узкая карточка не обрезает сумму: подпись переносится, кнопка растёт по высоте.
                  h="auto"
                  py={8}
                  styles={{ label: { whiteSpace: 'normal', overflow: 'visible', textAlign: 'center' } }}
                  onClick={() => pay.mutate({ code: product.code, withRecurring })}
                >
                  {owned
                    ? 'Уже куплено'
                    : `Оплатить ${formatRubles(product.priceKopecks)}${withRecurring ? ' и подключить автопродление' : ''}`}
                </Button>
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>
      <Text size="xs" c="var(--text-subtle)">
        Оплата через Robokassa, чек придёт на почту. Оплачивая, вы принимаете{' '}
        <Anchor href={OFFER_TERMS_URL} target="_blank" rel="noopener" size="xs">условия оферты</Anchor>.
      </Text>
    </Stack>
  );
}
